import { EyeOff, Send } from "lucide-react";

import { ConfirmationModal } from "@components/ConfirmationModal";

import { DOCTOR_ACCESS_COPY } from "@constants/doctor-access";

type ReleaseConfirmDialogProps = {
  open: boolean;
  /** The state being moved TO — true releases the case, false hides it. */
  releasing: boolean;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Confirmation before changing doctor visibility (MDRS-230).
 *
 * Releasing exposes a full case of PHI to another user, and hiding pulls a case
 * out from under someone who may be mid-report, so neither direction should be a
 * single unguarded click. Shared by the Update case modal and the case detail
 * Access card so the wording matches on both.
 *
 * Built on the house `ConfirmationModal` rather than a bespoke dialog, so the
 * button variants, focus handling and loading state stay consistent with every
 * other confirmation in the app. `type="warning"` gives hiding the destructive
 * treatment — it removes access someone already had.
 */
export const ReleaseConfirmDialog = ({
  open,
  releasing,
  isPending = false,
  onConfirm,
  onCancel,
}: ReleaseConfirmDialogProps) => (
  <ConfirmationModal
    open={open}
    onOpenChange={(next) => !next && onCancel()}
    type={releasing ? "info" : "warning"}
    icon={
      releasing ? (
        <Send className="size-9 text-primary" />
      ) : (
        <EyeOff className="size-9 text-destructive" />
      )
    }
    title={
      releasing
        ? DOCTOR_ACCESS_COPY.confirmReleaseTitle
        : DOCTOR_ACCESS_COPY.confirmHideTitle
    }
    description={
      releasing
        ? DOCTOR_ACCESS_COPY.confirmReleaseBody
        : DOCTOR_ACCESS_COPY.confirmHideBody
    }
    confirmButtonText={
      releasing
        ? DOCTOR_ACCESS_COPY.confirmReleaseAction
        : DOCTOR_ACCESS_COPY.confirmHideAction
    }
    cancelButtonText={DOCTOR_ACCESS_COPY.cancel}
    isLoading={isPending}
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
);
